import { useId, useState } from 'react'
import type { BlockAsk } from './blocks'

/**
 * The question a block asks before it is inserted, drawn by this product.
 *
 * One component for both surfaces that offer blocks — the '/' menu at the
 * caret and the toolbar's Insert menu — because a mechanism that exists twice
 * is a mechanism that will disagree with itself. The shell differs and the
 * question does not: the caller says which container this sits in, and the
 * form is the same row of `.label`, `.input`, the two buttons and the rule,
 * which is exactly the shape the link bar in the toolbar already has.
 *
 * Everything a `window.prompt` could not do is here: the rule is on screen
 * while the answer is being typed and is the field's accessible description,
 * Escape leaves without inserting, the button will not fire on an empty
 * answer, and a browser driving the editor can press it.
 */
export function BlockAskForm({
  ask,
  className,
  onAnswer,
  onCancel,
}: {
  ask: BlockAsk
  /** The row this belongs to: the toolbar's own, or the slash panel's. */
  className: string
  onAnswer: (answer: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState('')
  const id = useId()
  const ready = value.trim() !== ''

  return (
    <form
      className={className}
      onSubmit={(e) => {
        e.preventDefault()
        if (ready) onAnswer(value.trim())
      }}
    >
      <label className="label" htmlFor={id}>
        {ask.label}
      </label>
      <input
        id={id}
        className={`input${ask.mono ? ' mono' : ''}`}
        autoFocus
        value={value}
        placeholder={ask.placeholder}
        /*
          The rule is DESCRIBED by the field rather than wrapped in its label.
          A `<label>` around both makes the whole paragraph the control's
          accessible name, which a screen reader then reads back every time
          focus lands on it — the same decision the create dialog documents.
        */
        aria-describedby={`${id}-note`}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel()
        }}
      />
      <button className="btn btn--sm btn--tinted" disabled={!ready}>
        {ask.submitLabel}
      </button>
      <button type="button" className="btn btn--sm btn--ghost" onClick={onCancel}>
        Cancel
      </button>
      <p className="field__note" id={`${id}-note`}>
        {ask.note}
      </p>
    </form>
  )
}
